import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as Icons from '@components/Icons';

export interface RowMenuItem {
  /** Optional icon component (lucide-style). Receives `{ size }`. */
  icon?: React.ComponentType<{ size?: number }>;
  label: string;
  /** Visual tone. `'danger'` styles the row red for destructive actions. */
  tone?: string;
  /** Click handler. Closing the menu happens automatically before this fires. */
  onClick?: () => void;
  disabled?: boolean;
}

interface RowMenuProps {
  /** A `null` entry renders a divider between groups. */
  items: readonly (RowMenuItem | null)[];
}

/**
 * 3-dot row dropdown. Renders the menu via a portal anchored to the trigger
 * button's bounding rect — this dodges `overflow: hidden` on ancestor cards
 * and tables that were clipping the dropdown when it tried to escape the
 * row. Closes on outside click, scroll, resize, and Escape.
 */
export function RowMenu({ items }: RowMenuProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const reposition = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  };

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (menuRef.current?.contains(t)) return;
      if (btnRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onScrollOrResize() { setOpen(false); }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        className="icon-btn"
        data-variant="ghost"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label="Acciones"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Icons.More size={14} />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          className="row-menu"
          role="menu"
          style={{ position: 'fixed', top: pos.top, right: pos.right }}
          onClick={(e) => { e.stopPropagation(); }}
        >
          {items.map((item, idx) =>
            item === null ? (
              <div key={idx} className="row-menu-sep" />
            ) : (
              <button
                key={idx}
                role="menuitem"
                className="row-menu-item"
                data-tone={item.tone}
                disabled={item.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  item.onClick?.();
                }}
              >
                {item.icon && <item.icon size={13} />}
                <span>{item.label}</span>
              </button>
            ),
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
